export const BULK_UPLOAD_SCHEMA_VERSION = "2026-08-15";

export type BulkUploadField = {
  key: string;
  label: string;
  required?: boolean;
  aliases?: string[];
};

export type BulkUploadRequiredGroup = {
  key: string;
  label: string;
  keys: string[];
};

const field = (
  key: string,
  label: string,
  required = false,
  aliases: string[] = [],
): BulkUploadField => ({ key, label, required, aliases });

export const TRANSFER_BULK_UPLOAD_SCHEMA_ID = `payout-transfer-bulk-${BULK_UPLOAD_SCHEMA_VERSION}`;
export const BENEFICIARY_BULK_UPLOAD_SCHEMA_ID = `payout-beneficiary-bulk-${BULK_UPLOAD_SCHEMA_VERSION}`;

export const transferBulkUploadFields = [
  field("schema_version", "Schema Version"),
  field("funding_account_id", "Funding Account ID", true, [
    "account_id",
    "accountid",
    "fundingaccountid",
    "source_account_id",
    "sourceaccountid",
    "payout_account_id",
    "payoutaccountid",
  ]),
  field("funding_account_name", "Payout Account", false, [
    "payout_account",
    "payoutaccount",
    "source_account",
    "sourceaccount",
    "funding_account",
    "fundingaccount",
    "account",
  ]),
  field("beneficiary_id", "Beneficiary ID", false, [
    "beneficiaryid",
  ]),
  field("beneficiary_name", "Beneficiary Name", true, [
    "beneficiary",
    "beneficiaryname",
    "name",
  ]),
  field("beneficiary_account_number", "Beneficiary Account", true, [
    "beneficiary_account",
    "beneficiaryaccount",
    "beneficiary_account_number",
    "beneficiaryaccountnumber",
    "account_number",
    "accountnumber",
    "bene_account_number",
    "beneaccountnumber",
  ]),
  field("beneficiary_ifsc", "IFSC", true, [
    "ifsc",
    "beneficiary_ifsc",
    "beneficiaryifsc",
    "ifsc_code",
    "ifsccode",
  ]),
  field("amount", "Amount", true, [
    "amount_rupees",
    "amountrupees",
    "amount_inr",
    "amountinr",
  ]),
  field("amount_paise", "Amount Paise", false, [
    "amountpaise",
  ]),
  field("mode", "Method", true, [
    "method",
    "payment_method",
    "paymentmethod",
    "payout_method",
    "payoutmethod",
    "transfer_method",
    "transfermethod",
  ]),
  field("category", "Category", false),
  field("purpose", "Purpose", true, [
    "narration",
    "remarks",
  ]),
  field("client_reference_id", "Reference", true, [
    "reference",
    "reference_id",
    "referenceid",
    "clientreferenceid",
    "client_reference",
    "clientreference",
  ]),
] as const;

export const beneficiaryBulkUploadFields = [
  field("schema_version", "Schema Version"),
  field("funding_account_id", "Funding Account ID", true, [
    "account_id",
    "accountid",
    "fundingaccountid",
    "payout_account_id",
    "payoutaccountid",
  ]),
  field("funding_account_name", "Payout Account", false, [
    "payout_account",
    "payoutaccount",
    "funding_account",
    "fundingaccount",
    "account",
  ]),
  field("name", "Beneficiary Name", true, [
    "beneficiary_name",
    "beneficiaryname",
    "name_entered",
    "nameentered",
  ]),
  field("category", "Category", true, [
    "type",
  ]),
  field("mobile", "Mobile", false, [
    "mobile_number",
    "mobilenumber",
    "phone",
  ]),
  field("email", "Email", false),
  field("account_type", "Account Type", true, [
    "accounttype",
    "type",
  ]),
  field("account_number", "Account Number", true, [
    "accountnumber",
    "bank_account_number",
    "bankaccountnumber",
    "beneficiary_account_number",
    "beneficiaryaccountnumber",
    "beneficiary_bank_account_number",
    "beneficiarybankaccountnumber",
  ]),
  field("ifsc", "IFSC", true, [
    "ifsc_code",
    "ifsccode",
    "beneficiary_ifsc",
    "beneficiaryifsc",
  ]),
  field("bank_name", "Bank Name", true, [
    "bankname",
  ]),
  field("branch_name", "Branch Name", true, [
    "branch",
    "branchname",
  ]),
  field("remarks", "Remarks", false, [
    "purpose",
  ]),
] as const;

export const transferRequiredHeaderGroups: BulkUploadRequiredGroup[] = [
  {
    key: "funding_account",
    label: "Funding Account (Funding Account ID / Payout Account)",
    keys: ["funding_account_id", "funding_account_name"],
  },
  {
    key: "beneficiary",
    label: "Beneficiary (Beneficiary ID or beneficiary name + account + IFSC)",
    keys: ["beneficiary_id", "beneficiary_name", "beneficiary_account_number", "beneficiary_ifsc"],
  },
  {
    key: "amount",
    label: "Amount (Amount / Amount Paise)",
    keys: ["amount", "amount_paise"],
  },
  {
    key: "purpose",
    label: "Purpose",
    keys: ["purpose"],
  },
  {
    key: "reference",
    label: "Reference",
    keys: ["client_reference_id"],
  },
];

export const beneficiaryRequiredHeaderGroups: BulkUploadRequiredGroup[] = [
  {
    key: "funding_account",
    label: "Payout Account (Funding Account ID / Payout Account)",
    keys: ["funding_account_id", "funding_account_name"],
  },
  { key: "name", label: "Beneficiary Name", keys: ["name"] },
  { key: "category", label: "Category", keys: ["category"] },
  { key: "account_number", label: "Account Number", keys: ["account_number"] },
  { key: "ifsc", label: "IFSC", keys: ["ifsc"] },
  { key: "account_type", label: "Account Type", keys: ["account_type"] },
  { key: "bank_name", label: "Bank Name", keys: ["bank_name"] },
  { key: "branch_name", label: "Branch Name", keys: ["branch_name"] },
];

export const allowedBeneficiaryCategories = new Set([
  "vendor",
  "employee",
  "student",
  "scholarship",
  "refund",
  "reimbursement",
  "transport-vendor",
  "utility-provider",
  "others",
]);

export const allowedBeneficiaryAccountTypes = new Set([
  "savings",
  "current",
  "salary",
  "nre",
  "nro",
  "overdraft",
  "cash-credit",
]);

export const allowedTransferModes = new Set(["IMPS", "NEFT", "RTGS"]);

export const buildHeaderAliases = (fields: readonly BulkUploadField[]) =>
  fields.reduce<Record<string, string>>((aliases, item) => {
    const keys = [item.key, item.label, ...(item.aliases || [])];
    keys.forEach((key) => {
      aliases[normalizeUploadHeaderKey(key)] = item.key;
    });
    return aliases;
  }, {});

export const normalizeUploadHeaderKey = (value: unknown) =>
  String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

export const canonicalHeaders = (fields: readonly BulkUploadField[]) =>
  fields.map((item) => item.label);

export const missingRequiredHeaders = (
  detectedHeaders: string[],
  requiredGroups: BulkUploadRequiredGroup[],
) => {
  const detected = new Set(detectedHeaders);
  return requiredGroups
    .filter((group) => !group.keys.some((key) => detected.has(key)))
    .map((group) => ({ key: group.key, label: group.label }));
};

